/**
 * Vaste rechten voor de rollenmatrix. Nieuwe gates in de app = nieuwe sleutel hier + seed + check.
 */
export const APP_PERMISSION_KEYS = [
  "view_activity_log",
  "org_contacts_read_basis",
  "org_contacts_read_full",
  "org_contacts_edit",
  "dominicusgangers_view_all",
  "dominicusgangers_view_volunteers_only",
  "dominicusgangers_edit",
  "groups_manage_members_app",
  "groups_manage_members",
  "groups_set_chair",
  "groups_resolve_member_detail_requests",
  "groups_resolve_member_add_requests",
  "groups_submit_member_add_requests",
  "contacts_send_vcard_email",
  "contacts_duplicate",
  "beheer_mailerlite",
  "groups_view_secretary",
  "groups_manage_secretary",
] as const;

export type AppPermissionKey = (typeof APP_PERMISSION_KEYS)[number];

export type AppPermissionMeta = {
  key: AppPermissionKey;
  label: string;
  hint?: string;
  /** Groepering op de matrix-pagina */
  section: "contacts" | "dominicusgangers" | "groups" | "beheer";
};

export const APP_PERMISSION_META: AppPermissionMeta[] = [
  {
    key: "org_contacts_read_basis",
    section: "contacts",
    label: "Alle kaarten: naam, e-mail, telefoon (alleen lezen)",
  },
  {
    key: "org_contacts_read_full",
    section: "contacts",
    label: "Alle kaarten: volledig (alleen lezen)",
    hint: "Impliceert ook basis-lezen.",
  },
  {
    key: "org_contacts_edit",
    section: "contacts",
    label: "Alle kaarten bewerken",
    hint: "Organisatie-breed; niet alleen eigen kaarten.",
  },
  {
    key: "contacts_duplicate",
    section: "contacts",
    label: "Contactkaart dupliceren",
  },
  {
    key: "contacts_send_vcard_email",
    section: "contacts",
    label: "vCard per e-mail versturen",
  },
  {
    key: "dominicusgangers_view_all",
    section: "dominicusgangers",
    label: "Dominicusgangers: alles zien",
  },
  {
    key: "dominicusgangers_view_volunteers_only",
    section: "dominicusgangers",
    label: "Dominicusgangers: alleen vrijwilligers-wens",
    hint: "Vrijwilliger-coördinator.",
  },
  {
    key: "dominicusgangers_edit",
    section: "dominicusgangers",
    label: "Dominicusgangers bewerken",
  },
  {
    key: "groups_manage_members_app",
    section: "groups",
    label: "Groepsleden beheren (app-breed)",
  },
  {
    key: "groups_manage_members",
    section: "groups",
    label: "Groepsleden in een groep beheren",
  },
  {
    key: "groups_set_chair",
    section: "groups",
    label: "Voorzitter instellen",
  },
  {
    key: "groups_view_secretary",
    section: "groups",
    label: "Groepssecretaris zien",
  },
  {
    key: "groups_manage_secretary",
    section: "groups",
    label: "Groepssecretaris wijzigen",
    hint: "Alleen zinvol als de rol ook ‘zien’ heeft.",
  },
  {
    key: "groups_resolve_member_detail_requests",
    section: "groups",
    label: "Aanvragen contactgegevens afhandelen (secretaris)",
  },
  {
    key: "groups_resolve_member_add_requests",
    section: "groups",
    label: "Aanvragen lid toevoegen afhandelen",
  },
  {
    key: "groups_submit_member_add_requests",
    section: "groups",
    label: "Verzoek lid toevoegen indienen",
  },
  {
    key: "view_activity_log",
    section: "beheer",
    label: "Activiteitenlog en gebruikersbeheer",
  },
  {
    key: "beheer_mailerlite",
    section: "beheer",
    label: "MailerLite-beheer",
  },
];

/** Ingebouwde rol-sleutels (users.app_role / contacts.app_role). */
export const BUILTIN_APP_ROLE_KEYS = [
  "deelnemer",
  "lezer_basis",
  "lezer_volledig",
  "secretaris",
  "vrijwilliger_coordinator",
  "beheerder",
] as const;

export type BuiltinAppRoleKey = (typeof BUILTIN_APP_ROLE_KEYS)[number];

export type AppRoleDefinition = {
  key: string;
  name: string;
  description: string;
  is_builtin: boolean;
  is_default: boolean;
  sort_order: number;
};

export type RoleMatrixRow = {
  role: AppRoleDefinition;
  permissions: Record<AppPermissionKey, boolean>;
};

/** Startrechten voor ingebouwde rollen (server seed + client fallback zonder matrix-payload). */
export const BUILTIN_ROLE_PERMISSIONS: Record<BuiltinAppRoleKey, readonly AppPermissionKey[]> = {
  deelnemer: [],
  lezer_basis: ["org_contacts_read_basis"],
  lezer_volledig: ["org_contacts_read_basis", "org_contacts_read_full"],
  vrijwilliger_coordinator: ["dominicusgangers_view_volunteers_only"],
  secretaris: [
    "view_activity_log",
    "beheer_mailerlite",
    "org_contacts_read_basis",
    "org_contacts_read_full",
    "org_contacts_edit",
    "contacts_duplicate",
    "contacts_send_vcard_email",
    "dominicusgangers_view_all",
    "dominicusgangers_edit",
    "groups_manage_members_app",
    "groups_manage_members",
    "groups_set_chair",
    "groups_view_secretary",
    "groups_manage_secretary",
    "groups_resolve_member_detail_requests",
    "groups_resolve_member_add_requests",
  ],
  beheerder: [
    "view_activity_log",
    "beheer_mailerlite",
    "org_contacts_read_basis",
    "org_contacts_read_full",
    "org_contacts_edit",
    "contacts_duplicate",
    "contacts_send_vcard_email",
    "dominicusgangers_view_all",
    "dominicusgangers_edit",
    "groups_manage_members_app",
    "groups_manage_members",
    "groups_set_chair",
    "groups_view_secretary",
    "groups_manage_secretary",
    "groups_resolve_member_add_requests",
  ],
};
